import { TripHistories, TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';

/**
 * Returns true if the value is safe to use as a trip request ID in a URL path segment.
 * Rejects empty, whitespace-only, and values that could allow path traversal or injection.
 */
export function isValidTripRequestIdForPath(id: string | undefined): id is string {
  if (id == null || typeof id !== 'string') return false;
  const trimmed = id.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('..')) return false;
  return true;
}

// Assumes keys are mapped to ordered list of trip request.
// Orders trips for display using th following order SOLD, MATCHED, VERIFIED, PENDING.
// Ignores JUNK and CANCELED
export const orderTrips = (obj:{ [key: string | TripRequestStatus]: TripRequest[] }) => {
  let ret: TripRequest[] = [];
  if (obj.SOLD) { ret = [...ret, ...obj.SOLD]; }
  if (obj.MATCHED) { ret = [...ret, ...obj.MATCHED]; }
  if (obj.VERIFIED) { ret = [...ret, ...obj.VERIFIED]; }
  if (obj.PENDING) { ret = [...ret, ...obj.PENDING]; }
  return ret;
};

// Sorts TRS Based on status.
export const sortTripsByStatus = (list: TripRequest[]) => {
  const ret: { [key: string | TripRequestStatus]: TripRequest[] } = {};
  for (let i = 0; i < list.length; i += 1) {
    const trip = list[i];
    if (!ret[trip.status]) {
      ret[trip.status] = [];
    }
    ret[trip.status].push(trip);
  }
  return ret;
};

// Creates two arrays with one with past trips and one with active trips;
export const getPastAndActiveTrips = (list: TripRequest[]) => {
  const today = new Date();
  const pastTrips = [];
  const activeTrips = [];
  for (let i = 0; i < list.length; i += 1) {
    const trip = list[i];
    if (new Date(trip.returnDate) > today) {
      activeTrips.push(trip);
    } else {
      pastTrips.push(trip);
    }
  }
  return [pastTrips, activeTrips];
};

// Calculates the number of days since a given date
export const getDaysSince = (dateString: string): number => {
  const date = new Date(dateString);
  const timeDifference = Date.now() - date.getTime();
  return timeDifference / (1000 * 60 * 60 * 24);
};

const ACTIVE_STATUSES = new Set([
  TripRequestStatus.VerifiedStatus,
  TripRequestStatus.MatchedStatus,
  TripRequestStatus.SoldStatus,
  TripRequestStatus.ReferredStatus,
]);

const ARCHIVED_STATUSES = new Set([
  TripRequestStatus.JunkStatus,
  TripRequestStatus.CanceledStatus,
]);

// Filter a list of Trip Request into a list that only contains trips with a status of
// "VERIFIED", "MATCHED", "SOLD", "REFERRED", Or a PENDING trip that was created within the
// last 180 days.
export const filterPastTrips = (list: TripRequest[]): TripRequest[] => list.filter((trip) => {
  const daysSinceCreation = getDaysSince(trip.createdAt);

  const isActiveStatus = ACTIVE_STATUSES.has(trip.status);
  const isRecentPending = trip.status === TripRequestStatus.PendingStatus && daysSinceCreation <= 180;

  return isActiveStatus || isRecentPending;
});

// Filter a list of Trip Request into a list that only contains trips with a status of
// "Junk", "CANCELED", or a status of "PENDING" that was created over 180 days ago.
export const filterArchivedTrips = (list: TripRequest[]): TripRequest[] => list.filter((trip) => {
  const daysSinceCreation = getDaysSince(trip.createdAt);

  const isArchivedStatus = ARCHIVED_STATUSES.has(trip.status);
  const isOldPending = trip.status === TripRequestStatus.PendingStatus && daysSinceCreation > 180;

  return isArchivedStatus || isOldPending;
});

interface CanInviteCoTravelersOptions {
  tripHistories?: TripHistories;
  /** Sold match return dates; falls back to tripRequest.returnDate when omitted */
  soldMatchReturnDates?: string[];
}

const hasUpcomingTravelDate = (returnDate?: string, soldMatchReturnDates?: string[]): boolean => {
  const dates = soldMatchReturnDates?.length
    ? soldMatchReturnDates
    : [returnDate].filter((date): date is string => !!date);
  const now = Date.now();
  return dates.some((date) => new Date(date).getTime() > now);
};

const isActiveTrip = (tripRequestId: string, tripHistories?: TripHistories): boolean => (
  tripHistories?.activeTrips?.some((trip) => trip.id === tripRequestId) ?? false
);

/**
 * Whether the traveler can invite co-travelers on this trip request.
 * - MATCHED / VERIFIED: always allowed (even after travel dates / when archived)
 * - SOLD: allowed only while a sold travel date is still upcoming (hidden once Completed)
 * - CANCELED: never allowed
 * - Any other status: only while the trip is still active
 */
export const canInviteCoTravelers = (
  tripRequest: Pick<TripRequest, 'id' | 'status' | 'returnDate'> | undefined,
  options: CanInviteCoTravelersOptions = {},
): boolean => {
  if (!tripRequest) return false;

  switch (tripRequest.status) {
    case TripRequestStatus.CanceledStatus:
      return false;
    case TripRequestStatus.MatchedStatus:
    case TripRequestStatus.VerifiedStatus:
      return true;
    case TripRequestStatus.SoldStatus:
      return hasUpcomingTravelDate(tripRequest.returnDate, options.soldMatchReturnDates);
    default:
      return isActiveTrip(tripRequest.id, options.tripHistories);
  }
};
